<script setup lang="ts">
import { computed, onMounted, ref, watch, type Ref } from 'vue';
import Flow, { type FlowFile } from '@flowjs/flow.js';
import { getCsrfToken } from '@/helpers/auth.js';
import { apiUrl } from '@/helpers/api.js';
import { DocumentCollection, Document } from '@/models/Document';
import { getI18n } from '@/helpers/i18n';
import { globalVar } from '@/helpers/globals';
import { errToStr, fileExtToMime } from '@/helpers/tools';
import { createModel } from '@kp/js-model';
import type { KTGFileInputInputEvent } from '@kanton-thurgau/design-system/file-input';

type UploadEntry = {
    flowFile: FlowFile | null;
    document: Document | null;
    uploadProgress: number;
    uploadStarted: boolean;
    error?: string | null;
    errorFile?: File | null;
};

const props = withDefaults(
    defineProps<{
        autoStartUpload?: boolean;
        context?: string | null;
        sub_context?: string;
        parentId: number;
        entity?: string;
        readonly?: boolean;
        min?: number;
        max?: number;
        displayMin?: number;
        showDeletedSwitch?: boolean;
        decisionRulingId?: number | null;
        title?: string;
        showFiles?: boolean;
        tooltip?: string;
        additionalParams?: Record<string, any>;
        hideTitle?: boolean;
    }>(),
    {
        autoStartUpload: true,
        decisionRulingId: null,
        context: null,
        readonly: true,
        min: 0,
        max: 0,
        displayMin: 0,
        showDeletedSwitch: false,
        showFiles: true,
        additionalParams: () => ({}),
        hideTitle: false
    }
);

const emit = defineEmits(['upload-in-progress', 'file-uploaded', 'file-deleted']);

defineExpose({
    uploadFiles,
    reset,
    clear
});

const documents: DocumentCollection = new DocumentCollection();
/**
 * uploadEntries is a combination of a FlowFile and a Document entry:
 * It is needed to display documents as soon as they are added to the upload queue,
 * and display an upload progress bar.
 * After the upload, the Document is used for the display.
 */
const uploadEntries: Ref<UploadEntry[]> = ref([]);

const uploading = ref(false);
const fetching = ref(false);
const progress = ref(0);
const error: Ref<string | null> = ref(null);
const showDeletedFiles = ref(false);

// calculated error value that is displayed: there are many
// ways error could happen, and we decide here what we display
const displayError = computed(() => {
    if (error.value) {
        return error.value;
    }
    if (uploadEntries.value.length < props.min) {
        return getI18n().t('min_error_n', { count: props.min });
    }

    return null;
});

const canUpload = computed(() => {
    if (props.readonly) {
        return false;
    }

    if (!props.max || props.max === 0) {
        return true;
    }

    if (uploadedFilesCount.value < props.max) {
        return true;
    }

    return false;
});

const tooltipId = computed(() => {
    return (props.context || '') + '-' + (props.sub_context || '') + props.parentId + '-' + new Date().getTime();
});

const uploadedFilesCount = computed(() => {
    if (documents.length() === 0) {
        return 0;
    }

    let count = 0;
    documents.each((doc) => {
        if (!doc.deleted) {
            count++;
        }
    });

    return count;
});

const uploadEntriesForDisplay = computed(() => {
    return uploadEntries.value.filter((entry) => {
        if (entry.flowFile) {
            return true;
        }
        if (showDeletedFiles.value) {
            return true;
        }
        if (!entry.document?.deleted) {
            return true;
        }
        if (entry.error) {
            return true;
        }
        return false;
    });
});

const title = computed(() => {
    if (props.title) {
        return props.title;
    }

    const suffix = props.displayMin > 0 || props.min > 0 ? ' *' : '';

    if (props.sub_context) {
        return getI18n().t(props.sub_context) + suffix;
    }
    return getI18n().t(props.context || '') + suffix;
});

watch(uploading, (newVal, oldVal) => {
    if (newVal !== oldVal) {
        emit('upload-in-progress', newVal);
    }
});

onMounted(() => {
    fetchExistingDocuments();
    initFlow();
});

let flow: Flow;
function initFlow() {
    flow = new Flow({
        singleFile: false,
        simultaneousUploads: 1,
        target: apiUrl('/document/uploadFile'),
        testChunks: false,
        chunkSize: 5 * 1024 * 1024, // 5 MB junks, for faster uploads of bigger files
        allowDuplicateUploads: true,
        headers: {
            'X-CSRF-Token': getCsrfToken()
        }
    });
    flow.on('fileAdded', onFlowFileAdded);
    flow.on('filesSubmitted', onFilesSubmitted);
}

function onFileInputFileAdded(ev: KTGFileInputInputEvent) {
    if (ev.detail.ignoredFiles?.length > 0) {
        ev.detail.ignoredFiles.forEach((file: File) => {
            uploadEntries.value.push({
                flowFile: null,
                document: null,
                uploadProgress: 0,
                uploadStarted: false,
                errorFile: file,
                error: `${getI18n().t('format_not_supported')}: ${file.name}`
            });
        });
    }
    ev.detail.files.forEach((file: File) => {
        flow.addFile(file);
    });
}

function onFlowFileAdded(file: FlowFile) {
    error.value = null;
    if (!canUpload.value || props.readonly) {
        error.value = getI18n().t('no_more_upload_to_category');
        return false;
    }

    if (!checkAllowedFileType(file)) {
        error.value = getI18n().t('format_not_supported');
        return false;
    }

    if (file.size > 100 * 1000 * 1000) {
        uploadEntries.value.push({
            flowFile: null,
            document: null,
            uploadProgress: 0,
            uploadStarted: false,
            errorFile: file.file,
            error: `${getI18n().t('err_msg_file_too_large')}`
        });
        return false;
    }

    // create a flowFile entry to display the upload:
    uploadEntries.value.push({
        flowFile: file,
        document: null,
        uploadProgress: 0,
        uploadStarted: false,
        error: null
    });
}

function checkAllowedFileType(file: FlowFile) {
    // check for file tyspe
    const allowedFileTypes = getAllowedFileTypes();
    const fileExtension = file.name.split('.').pop() || '';

    // check if FileType is in Array of allowedFileTypes
    if (allowedFileTypes?.includes(fileExtension.toLowerCase()) === false) {
        error.value = getI18n().t('filetype_not_allowed') + allowedFileTypes.toString();
        return false;
    }

    return true;
}

function getAllowedFileTypes(): string[] {
    const allowedFileTypesConfig = globalVar('allowed_file_types');

    if (props.context && allowedFileTypesConfig[props.context]) {
        if (props.sub_context && allowedFileTypesConfig[props.context][props.sub_context]) {
            return allowedFileTypesConfig[props.context][props.sub_context] || [];
        }

        return allowedFileTypesConfig[props.context]['default'] || allowedFileTypesConfig['default'] || [];
    }

    return (allowedFileTypesConfig['default'] || []).sort();
}

function downloadUrl(doc: Document | null) {
    if (!doc) {
        return '';
    }
    if (doc.deleted) {
        return '';
    }
    return apiUrl('/Document/downloadDocument', {
        document_id: doc.id
    });
}

function canDeleteFile(item: Document | null) {
    if (!item) {
        return true;
    }
    if (props.readonly || item.deleted) {
        return false;
    }
    if (item.sub_context && item.sub_context !== 'baugesuchformular' && item.sub_context !== props.sub_context) {
        return false;
    }

    return true;
}

async function onDeleteFile(entry: UploadEntry | null) {
    if (!entry) {
        return;
    }
    const file = entry.document;

    if (!file) {
        if (entry.flowFile) {
            const newFiles = [];
            for (let i = 0; i < flow.files.length; i++) {
                if (flow.files[i].uniqueIdentifier !== entry.flowFile.uniqueIdentifier) {
                    newFiles.push(flow.files[i]);
                }
            }
            flow.files = newFiles;
        }
        const newEntries = [];
        const existingEntries = uploadEntries.value;
        for (let i = 0; i < existingEntries.length; i++) {
            if (existingEntries[i] !== entry) {
                newEntries.push(existingEntries[i]);
            }
        }
        uploadEntries.value = newEntries;
        return;
    }

    error.value = null;
    try {
        await file.destroy();
        fetchExistingDocuments();
    } catch (e) {
        error.value = errToStr(e);
    }
}

function onFilesSubmitted() {
    if (props.autoStartUpload) {
        onUploadFiles();
    }
}

async function onUploadFiles() {
    try {
        await uploadFiles();
    } catch (err) {
        try {
            const json = JSON.parse(err as string);
            if (json?.error) {
                error.value = json.error.msg;
            }
        } catch (innerErr) {
            error.value = getI18n().t('upload_file_error');
        }
    } finally {
        uploading.value = false;
    }
}

/**
 * starts the file upload, and returns a promise to the
 * uploaded Document models: The emitted document models are already processed / stored by
 * the backend.
 *
 *
 */
function uploadFiles(): Promise<Document[] | null> {
    return new Promise((resolve) => {
        if (flow.files.length === 0) {
            return resolve(null);
        }
        progress.value = 0;
        uploading.value = true;
        flow.opts.query = {
            ...props.additionalParams,
            parent_entity: props.entity,
            parent_id: props.parentId,
            parent_context: props.context,
            decision_ruling_id: props.decisionRulingId || '',
            sub_context: props.sub_context || ''
        };

        // remove previous listeners, if set:
        flow.off('complete');
        flow.off('error');
        flow.off('progress');
        flow.off('fileProgress');
        flow.off('fileSuccess');
        error.value = null;

        let fn = () => {
            flow.off('complete', fn);
            // on upload end, we emit the final Document models
            const createdDocuments = uploadEntries.value
                .filter((entry) => entry.document !== null)
                .map((entry) => entry.document!);
            emit('file-uploaded', createdDocuments);
            reset();
            resolve(createdDocuments);
        };

        flow.on('complete', fn);
        flow.on('error', (message, file) => {
            const uploadEntry = uploadEntries.value.find(
                (entry) => entry.flowFile?.uniqueIdentifier === file.uniqueIdentifier
            );
            if (uploadEntry) {
                uploadEntry.error = message;
            }
        });

        flow.on('progress', () => {
            progress.value = Math.round((flow.sizeUploaded() / flow.getSize()) * 100);
        });

        // update the individual upload entrie's progress, to update the progess bar:
        flow.on('fileProgress', (file: FlowFile) => {
            const uploadEntry = uploadEntries.value.find(
                (entry) => entry.flowFile?.uniqueIdentifier === file.uniqueIdentifier
            );
            if (uploadEntry) {
                uploadEntry.uploadProgress = Math.round((file.sizeUploaded() / file.size) * 100);
            }
        });
        flow.on('fileSuccess', (file: FlowFile, message: string) => {
            const response = JSON.parse(message);
            if (response?.documents?.length > 0) {
                const docResponse = response.documents[0];
                const uploadEntry = uploadEntries.value.find(
                    (entry) => entry.flowFile?.uniqueIdentifier === file.uniqueIdentifier
                );
                if (uploadEntry) {
                    uploadEntry.document = createModel(Document, docResponse);
                    uploadEntry.document.setPhantom(false);
                    uploadEntry.flowFile = null;
                }
            }
        });

        uploadEntries.value.forEach((entry) => {
            entry.uploadStarted = true;
        });

        // start the upload queue!
        flow.upload();
    });
}

function reset() {
    flow.cancel();
    uploading.value = false;
    progress.value = 0;
    fetching.value = false;
    error.value = null;
}

function clear() {
    documents.clear();
    uploadEntries.value = [];
}

async function fetchExistingDocuments() {
    if (props.showFiles === false) {
        return;
    }
    if (uploading.value == true) {
        return;
    }
    if (!props.parentId) {
        return;
    }
    fetching.value = true;
    await documents.query({
        parent_entity: props.entity,
        parent_id: props.parentId,
        context: props.context,
        decision_ruling_id: props.decisionRulingId || '',
        sub_context: props.sub_context || ''
    });
    const newUploadEntries: UploadEntry[] = [];
    documents.each((doc) => {
        newUploadEntries.push({
            document: doc,
            flowFile: null,
            uploadProgress: 0,
            uploadStarted: false,
            error: null
        });
    });
    uploadEntries.value = newUploadEntries;
    fetching.value = false;
}
</script>

<template>
    <div>
        <span v-if="!props.hideTitle" class="ktg-body fileupload-label ktg-inner-box-padding pb-0_5rem">
            {{ title }} <ktg-tooltip-button :id="tooltipId" v-if="props.tooltip" class="ml-0_5rem"></ktg-tooltip-button>
            <ktg-tooltip :for="tooltipId" v-html="props.tooltip"></ktg-tooltip>
        </span>
        <ktg-file-input
            ref="filedrop"
            multiple
            @input="onFileInputFileAdded"
            :accept="getAllowedFileTypes().map(fileExtToMime).join(',')"
            :error="displayError"
            :readonly="props.readonly"
        >
            <ktg-file-input-instructions>{{ $t('drop_or_browse') }}</ktg-file-input-instructions>

            <ktg-file-input-hint>
                <div class="supported-files">{{ $t('required_filecount', { count: displayMin }) }}</div>
                <div class="supported-files">
                    {{ $t('supported_files') }}: <span class="uppercase">{{ getAllowedFileTypes().join(', ') }}</span>
                </div>
                <div class="supported-files">{{ $t('max_filesize') }}: 100MB</div>
            </ktg-file-input-hint>

            <ktg-upload-files>
                <ktg-upload-file
                    v-for="entry in uploadEntriesForDisplay"
                    :key="entry.document?.id || entry.flowFile?.uniqueIdentifier"
                    :removable="canDeleteFile(entry.document)"
                    :loading="entry.flowFile !== null && !entry.error && entry.uploadStarted"
                    :error="entry.error !== null"
                    :error-message="errToStr(entry.error)"
                    :href="downloadUrl(entry.document)"
                    @remove="onDeleteFile(entry)"
                >
                    <template v-if="entry.document?.id">
                        <span :class="{ deleted: entry.document.deleted !== null }">
                            {{ entry.document.filename_original }}
                        </span>
                    </template>
                    <template v-if="entry.flowFile">
                        {{ entry.flowFile.name }}
                    </template>
                    <template v-if="entry.errorFile">
                        <span>{{ entry.errorFile.name }} </span>
                    </template>
                </ktg-upload-file>
            </ktg-upload-files>
        </ktg-file-input>
        <div v-if="showDeletedSwitch" class="my-1rem">
            <ktg-labelled-choice>
                <ktg-toggle
                    input-id="deleted-switch"
                    :disabled="uploading || fetching"
                    :checked="showDeletedFiles"
                    @change="(e: InputEvent) => (showDeletedFiles = (e.target as HTMLInputElement).checked)"
                ></ktg-toggle>
                <ktg-label for="deleted-switch">{{ $t('show_deleted_files') }}</ktg-label>
            </ktg-labelled-choice>
        </div>
        <!-- error display: -->
        <ktg-field-error v-if="displayError" class="error-margin">{{ displayError }}</ktg-field-error>
    </div>
</template>

<style lang="scss" scoped>
.error-margin {
    margin-top: 4px;
    margin-bottom: 0;
}

.drop-area {
    background-color: white;
    border: 2px dotted var(--ktg-color-neutral-03);
    height: 8rem;
    padding: 1rem;
    &.dragover {
        border: 2px dotted var(--ktg-color-focus);
    }

    .infotext {
        font-size: 0.75rem;
    }
    .supported-files {
        font-size: 0.75rem;
        color: var(--ktg-color-neutral-05);
    }
}

.fileupload-label {
    font-weight: 500;
    color: var(--ktg-color-text);
    display: inline-flex;
    align-items: center;
}

.uploadedFileEntry {
    background-color: white;
    padding: 0.5rem 1rem;
    .deleted {
        text-decoration: line-through;
        color: var(--ktg-color-neutral-05);
    }
}
</style>
